import type { CheckResult } from './types';

/**
 * Collapses the per-round results of one cycle into one verdict per link.
 *
 * A straight port of Android's `domain/usecase/RoundAggregator.kt`, and the one
 * piece of that pipeline that ports **without loss** — it never looks at a status
 * code, only at whether a round succeeded, which is exactly what this app has.
 *
 * A link that fails once has not been proven dead. Mobile signal drops, DNS
 * blips, and sockets the carrier's NAT quietly forgot all produce a failure that
 * says nothing about the link. Running several rounds and only concluding "dead"
 * when every round failed is what separates *this link is blocked in this market*
 * — the thing the whole system exists to find — from *the handset lost signal for
 * two seconds*.
 *
 * Pure on purpose: no fetch, no timers, no clock. This is where a mistake
 * corrupts the data rather than merely slowing it down, so it lives somewhere it
 * can be exhaustively tested.
 */
export interface Aggregated {
  /** One row per link — this is what gets submitted. */
  results: CheckResult[];
  /**
   * Never submitted. It exists so the caller can **count** what it threw away:
   * a rule that silently discards data and cannot be measured is one nobody will
   * ever notice going wrong.
   */
  dropped: CheckResult[];
}

/**
 * `rounds` holds each round's results in order. Round 1 covers every link; later
 * rounds carry only the links still failing, so a link may appear in fewer
 * rounds than another. `configuredRounds` is how many rounds the cycle was
 * *supposed* to run — the yardstick for `dropped`.
 */
export function aggregate(rounds: readonly (readonly CheckResult[])[], configuredRounds: number): Aggregated {
  // A Map, not a plain object: insertion order is preserved for string keys
  // either way, but a link id like `"__proto__"` or `"constructor"` from the
  // backend would collide with Object.prototype. It also keeps the dashboard's
  // order the fetched order rather than whatever numeric-key sorting an object
  // would impose — these ids are numeric strings, which objects reorder.
  const attempts = new Map<string, CheckResult[]>();
  for (const round of rounds) {
    for (const result of round) {
      const tries = attempts.get(result.link.id);
      if (tries) tries.push(result);
      else attempts.set(result.link.id, [result]);
    }
  }

  const results: CheckResult[] = [];
  const dropped: CheckResult[] = [];

  for (const tries of attempts.values()) {
    const firstSuccess = tries.find((t) => t.healthStatus === 'healthy');
    if (firstSuccess) {
      // One success is the whole proof. Later rounds failing does not un-prove
      // it, and the numbers reported are the successful round's — attaching a
      // failure's timing to a link we are calling healthy would be a lie in the
      // data.
      results.push(firstSuccess);
    } else if (tries.length >= configuredRounds) {
      // Failed every round it was supposed to get: a real verdict. The last
      // attempt is the freshest evidence and the one an operator would go and
      // reproduce.
      results.push(tries[tries.length - 1] as CheckResult);
    } else {
      // Still failing, but the cycle ended before this link had its full
      // complement of rounds — the deadline fired, or the page went to the
      // background mid-run. This is exactly the false positive that rounds exist
      // to remove, and the submit body carries no round count, so the backend
      // could not tell it apart from a link that failed all of them. Reporting
      // nothing this cycle is honest; reporting "dead" is not.
      dropped.push(tries[tries.length - 1] as CheckResult);
    }
  }

  return { results, dropped };
}

/** Links with no verdict yet — what the next round re-probes. */
export function stillFailing(results: readonly CheckResult[]): CheckResult['link'][] {
  return results.filter((r) => r.healthStatus !== 'healthy').map((r) => r.link);
}
